(() => {
  if (window.aurelianeRecommendationsReady) return;
  window.aurelianeRecommendationsReady = true;
  const sectionHTML = (url, signal) => fetch(url, { credentials: 'same-origin', signal }).then(response => {
    if (!response.ok) throw new Error('Section unavailable');
    return response.text();
  }).then(html => new DOMParser().parseFromString(html, 'text/html'));
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('aureliane-complete-look')) {
    customElements.define('aureliane-complete-look', class extends HTMLElement {
      connectedCallback() {
        if (this.unsubscribe || typeof subscribe !== 'function') return;
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (event.data.sectionId !== this.closest('product-info')?.dataset.section || !event.data.variant) return;
          this.refresh(event.data.variant.id);
        });
      }
      async refresh(variantId) {
        this.controller?.abort();
        this.controller = new AbortController();
        const url = new URL(this.dataset.sourceUrl, location.origin);
        url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', 'aureliane-complete-look');
        this.setAttribute('aria-busy', 'true');
        try {
          const html = await sectionHTML(url, this.controller.signal);
          const next = html.querySelector('aureliane-complete-look');
          if (!next) return;
          this.innerHTML = next.innerHTML;
          this.hidden = next.hidden;
          this.dataset.finish = next.dataset.finish.trim();
          document.querySelectorAll('.aur-recommendations .aur-card').forEach(card => {
            card.querySelector(`.aur-card__swatch--${this.dataset.finish}:not(.aur-card__swatch--unavailable)`)?.click();
          });
        } catch (error) {
          if (error.name !== 'AbortError') this.removeAttribute('aria-busy');
        } finally {
          this.removeAttribute('aria-busy');
        }
      }
      disconnectedCallback() {
        this.unsubscribe?.();
        this.unsubscribe = null;
        this.controller?.abort();
      }
    });
  }

  const root = document.querySelector('[data-aur-recommendations]');
  const setupRail = rail => {
    const track = rail.querySelector('.aur-rail__track');
    if (!track || rail.dataset.ready) return;
    rail.dataset.ready = 'true';
    const previous = rail.querySelector('[data-aur-prev]');
    const next = rail.querySelector('[data-aur-next]');
    const update = () => {
      const atEnd = track.scrollWidth - track.clientWidth - track.scrollLeft < 3;
      previous.disabled = track.scrollLeft < 3;
      next.disabled = atEnd;
      rail.querySelector('.aur-rail__controls').hidden = track.scrollWidth <= track.clientWidth + 3;
    };
    const move = direction => track.scrollBy({ left: direction * track.clientWidth * .9, behavior: reduceMotion() ? 'instant' : 'smooth' });
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(update).observe(track);
    update();
  };
  if (root) {
    root.querySelectorAll('[data-aur-rail]:not([hidden])').forEach(setupRail);
    let history = [];
    const storageKey = 'aureliane_recent_products_v1';
    const now = Date.now();
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(saved)) history = saved.filter(item => item && typeof item.handle === 'string' && /^[\p{L}\p{N}_-]+$/u.test(item.handle) && Number.isFinite(item.seenAt) && now - item.seenAt < 30 * 24 * 60 * 60 * 1000 && now >= item.seenAt);
    } catch (error) {
      history = [];
    }
    const previous = [...new Map(history.filter(item => item.handle !== root.dataset.currentHandle).map(item => [item.handle, item])).values()].slice(0, 8);
    try {
      localStorage.setItem(storageKey, JSON.stringify([{ handle: root.dataset.currentHandle, seenAt: now, audience: root.dataset.audience || '' }, ...previous, ...history.filter(item => item.handle !== root.dataset.currentHandle && !previous.some(old => old.handle === item.handle))].slice(0, 20)));
    } catch (error) {}
    const loadRecent = async () => {
      const rail = root.querySelector('[data-aur-recent]');
      const track = rail?.querySelector('.aur-rail__track');
      if (!track || !previous.length) return;
      const base = root.dataset.root.endsWith('/') ? root.dataset.root : `${root.dataset.root}/`;
      const cards = await Promise.all(previous.map(async item => {
        try {
          const url = new URL(`${base}products/${encodeURIComponent(item.handle)}`, location.origin);
          url.searchParams.set('section_id', 'aureliane-recent-card');
          if (item.audience === 'women') url.searchParams.set('view', 'femei');
          const html = await sectionHTML(url);
          const card = html.querySelector('aureliane-collection-card');
          if (!card || card.dataset.productId === root.dataset.sourceId) return null;
          const li = document.createElement('li');
          li.className = 'aur-rail__item';
          li.append(card);
          return li;
        } catch (error) { return null; }
      }));
      const visible = cards.filter(Boolean);
      if (!visible.length) return;
      track.replaceChildren(...visible);
      rail.hidden = false;
      setupRail(rail);
    };
    if (previous.length) {
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect();
        loadRecent();
      }, { rootMargin: '500px' });
      observer.observe(root);
    }
  }

  const dialog = document.getElementById('AurQuickDialog');
  if (!dialog) return;
  const body = dialog.querySelector('[data-aur-quick-body]');
  const status = dialog.querySelector('[data-aur-quick-status]');
  let trigger = null;
  let request = null;
  let submitting = false;
  const showError = () => {
    status.textContent = dialog.dataset.error;
    const link = document.createElement('a');
    link.className = 'aur-quick__details';
    link.href = trigger?.closest('.aur-card')?.querySelector('[data-aur-product-link]')?.href || location.href;
    link.textContent = dialog.dataset.addLabel;
    body.replaceChildren(link);
  };
  const syncVariant = form => {
    const select = form.querySelector('[data-aur-quick-variant]');
    const option = select.selectedOptions[0];
    if (!option) return;
    body.querySelector('[data-aur-quick-price]').textContent = option.dataset.price;
    const image = body.querySelector('.aur-quick__image');
    if (image && option.dataset.image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
      image.alt = option.dataset.alt || image.alt;
    }
    const detail = body.querySelector('.aur-quick__details');
    const href = new URL(detail.href);
    href.searchParams.set('variant', option.value);
    detail.href = href.href;
    form.querySelector('button[type="submit"]').disabled = option.disabled;
  };
  const close = () => {
    if (submitting) return;
    request?.abort();
    dialog.close();
    trigger?.focus();
  };
  dialog.querySelector('[data-aur-quick-close]').addEventListener('click', close);
  dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) close();
  });
  dialog.addEventListener('change', event => {
    if (event.target.matches('[data-aur-quick-variant]')) syncVariant(event.target.form);
  });
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-aur-quick-add]');
    if (!button) return;
    const href = button.closest('.aur-card')?.querySelector('[data-aur-product-link]')?.href;
    if (!href) return;
    event.preventDefault();
    request?.abort();
    request = new AbortController();
    trigger = button;
    body.replaceChildren();
    status.textContent = dialog.dataset.loading;
    dialog.querySelector('[data-aur-view-cart]').hidden = true;
    dialog.setAttribute('aria-label', dialog.dataset.loading);
    dialog.removeAttribute('aria-labelledby');
    if (!dialog.open) dialog.showModal();
    const url = new URL(href);
    url.searchParams.set('section_id', 'aureliane-quick-add');
    try {
      const html = await sectionHTML(url, request.signal);
      const content = html.querySelector('[data-aur-quick-content]');
      if (!content) { showError(); return; }
      body.replaceChildren(content);
      status.textContent = '';
      dialog.removeAttribute('aria-label');
      dialog.setAttribute('aria-labelledby', 'AurQuickTitle');
      const select = content.querySelector('[data-aur-quick-variant]');
      if (select && !select.hidden) select.focus();
      else content.querySelector('button[type="submit"]').focus();
    } catch (error) { if (error.name !== 'AbortError') showError(); }
  });
  dialog.addEventListener('submit', async event => {
    const form = event.target.closest('[data-aur-quick-form]');
    if (!form) return;
    event.preventDefault();
    if (submitting) return;
    submitting = true;
    const button = form.querySelector('button[type="submit"]');
    const error = form.querySelector('[data-aur-quick-error]');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    error.hidden = true;
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const data = new FormData(form);
    if (cart?.getSectionsToRender) {
      data.append('sections', cart.getSectionsToRender().map(section => section.id).join(','));
      data.append('sections_url', location.pathname);
      cart.setActiveElement?.(trigger);
    }
    try {
      const url = new URL(form.action);
      url.pathname = `${url.pathname}.js`;
      const response = await fetch(url, { method: 'POST', body: data, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.status === 422 ? dialog.dataset.unavailable : dialog.dataset.error);
      if (cart?.renderContents && result.sections) {
        dialog.close();
        cart.renderContents(result);
      } else {
        status.textContent = dialog.dataset.added;
        dialog.querySelector('[data-aur-view-cart]').hidden = false;
      }
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'aureliane-recommendations', productVariantId: data.get('id'), cartData: result });
    } catch (exception) {
      error.textContent = exception.message || dialog.dataset.error;
      error.hidden = false;
    } finally {
      submitting = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });
})();
